import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { Controller, useForm, UseFormReturn } from "react-hook-form"
import { useCreatePersonMutation } from "../../../../../../features/peopleApi"
import { roleDepartment } from "../../../../../lib/atecos/dto"
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice"
import { useAppDispatch } from "../../../../../lib/redux/hooks"
import { groupBy } from "../../../../../lib/utils"
import { colors } from "../../../../../ui/colors"
import { ChevronIcon } from "../../../../../ui/icons/chevron"
import { LinkedinIcon } from "../../../../../ui/icons/linkedin"
import { PhoneCallIcon } from "../../../../../ui/icons/phoneCall"
import Button from "../../../../../ui/molecules/button"
import Input from "../../../../../ui/molecules/input"
import { SelectCustom } from "../../../../../ui/molecules/select"
import Toggle from "../../../../../ui/molecules/toggle"
import { CompanyDTO } from "../../dto"
import { NewEmployeeFormValues, newEmployeeSchema } from "./validation"
import { PersonEmails } from "../../../people/editPerson/personEmails"
import { UpdatePersonFormValues } from "../../../people/editPerson/validation"
import { useParams } from "react-router-dom"

interface Props {
    navigate: (section: 'list' | 'new' | 'edit') => void;
    company: CompanyDTO;
    inTargetList: boolean
}

export function CreatePerson(props: Props) {
    const { navigate, company, inTargetList } = props
    const dispatch = useAppDispatch()

    const { targetId } = useParams()

    const form = useForm<NewEmployeeFormValues>({
        resolver: zodResolver(newEmployeeSchema),
        defaultValues: {},
    });


    useEffect(() => {
        if (company) {
            const formValues: Partial<NewEmployeeFormValues> = {
                name: '',
                surname: '',
                role: undefined,
                phone: '',
                emails: [''],
                linkedinProfile: '',
                privacy: false,
                companyName: company.name,
                companyVat: company.vat,
                companyPhones: company.phones,
            };
            form.reset(formValues);
        }
    }, [company, form]);

    const [createPerson] = useCreatePersonMutation();

    const handleSubmit = async (data: NewEmployeeFormValues) => {
        try {
            const response = await createPerson({
                data: data,
                companyId: company.id,
                addToTarget: targetId || undefined
            }).unwrap();
            if (response?.response.creationStatus.creationState === "DUPLICATE") {
                dispatch(addBannerWithTimeout({
                    message: "Dipendente già presente in azienda",
                    variant: "warning"
                }));
            }
            else {
                dispatch(addBannerWithTimeout({
                    message: "Dipendente creato con successo",
                    variant: "success"
                }));
                navigate('list');

            }
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: "Errore nella creazione del dipendente",
                variant: "error"
            }));
        }
    };

    return (
        <div className="px-4">
            <div className="border-y border-neutral-200 py-3 flex items-center justify-between">
                <div className="flex items-center gap-1 cursor-pointer" onClick={() => navigate('list')}>
                    <ChevronIcon color={colors.neutral[400]} size={24} direction="l" />
                    <span className="text-text-sm font-semibold text-neutral-700">Torna alla lista</span>
                </div>
                <Button
                    size={"sm"}
                    iconPosition={"left"}
                    variant={"outline"}
                    color={"blue"}
                    label="Aggiungi contatto"
                    onClick={() => form.handleSubmit(handleSubmit)()}
                />
            </div>
            <div
                className={"w-full py-4 flex flex-col gap-4 transition-all"}
            >
                <div className="flex gap-3 items-center">
                    <Controller
                        name="name"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <Input
                                label="Nome"
                                value={field.value || ''}
                                onChangeText={field.onChange}
                                error={!!fieldState.error}
                                supportingText={fieldState.error?.message || ''}
                            />
                        )}
                    />
                    <Controller
                        name="surname"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <Input
                                label="Cognome"
                                value={field.value || ''}
                                onChangeText={field.onChange}
                                error={!!fieldState.error}
                                supportingText={fieldState.error?.message || ''}
                            />
                        )}
                    />
                </div>
                <PersonEmails form={form as UseFormReturn<UpdatePersonFormValues | NewEmployeeFormValues>} isInTargetList={inTargetList} />
                <Controller
                    name="role"
                    control={form.control}
                    render={({ field }) => (
                        <SelectCustom
                            label="Ruolo"
                            isClearable
                            onChange={field.onChange}
                            defaultValue={field.value}
                            placeholder={"Seleziona un ruolo"}
                            menuPortalTargetIsNotBody
                            options={groupBy(roleDepartment, 'role').map(role => ({ label: role.key, value: role.key }))}
                        />
                    )}
                />
                <Controller
                    name="phone"
                    control={form.control}
                    render={({ field, fieldState }) => (
                        <Input
                            label="Smartphone"
                            value={field.value || ''}
                            onChangeText={field.onChange}
                            error={!!fieldState.error}
                            supportingText={fieldState.error?.message || ''}
                        />
                    )}
                />
                {/* <PersonPhones list={[...peopleState.newPersonRequest.phones!]} errors={peopleState.errors.validateCreate.phone} handler={list => dispatch(setNewPersonRequestPhone([...list]))} operationType={"create"} /> */}
                <Controller
                    name="linkedinProfile"
                    control={form.control}
                    render={({ field }) => (
                        <Input
                            label="Linkedin"
                            value={field.value || ''}
                            onChangeText={field.onChange}
                            startIcon={<LinkedinIcon color={""} size={0} />}
                        />
                    )}
                />
                <Controller
                    name="privacy"
                    control={form.control}
                    render={({ field }) => (
                        <Toggle
                            placeholder="Permessi e privacy"
                            checked={field.value ?? false}
                            onChange={field.onChange}
                        />
                    )}
                />
                <Input label="Azienda" value={form.watch("companyName")} disabled error={!!form.formState.errors.companyName} supportingText={form.formState.errors.companyName?.message} />
                <Input label="P. IVA" value={form.watch("companyVat")} disabled error={!!form.formState.errors.companyVat} supportingText={form.formState.errors.companyVat?.message} />
                {
                    form.watch("companyPhones")?.map((phone, index) => (
                        <div className="flex gap-3" key={phone.phone}>
                            <Input label="Telefono azienda" value={phone.phone || ''} disabled startIcon={<PhoneCallIcon color={""} size={0} />} error={!!form.formState.errors.companyPhones?.[index]?.phone} supportingText={form.formState.errors.companyPhones?.[index]?.phone?.message} />
                            <Input label="Note" value={phone.note || ''} disabled startIcon={<PhoneCallIcon color={""} size={0} />} error={!!form.formState.errors.companyPhones?.[index]?.note} supportingText={form.formState.errors.companyPhones?.[index]?.note?.message} />
                        </div>
                    ))
                }
            </div>
        </div>
    )
}